import { FormInstance } from "antd";
import { SorterResult } from "antd/es/table/interface";
import { Error } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BasedObservedViewModel } from "app/based/basedObservedViewModel/BasedObservedViewModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { RoleManagementInjection } from "features/roleManagement/di/RoleManagementInjection";
import { RoleActionRequestEntity } from "features/roleManagement/domain/entities/actions/RoleActionRequestEntity";
import { DetailRoleEntity } from "features/roleManagement/domain/entities/detail/DetailRoleEntity";
import { RoleRequestEntity } from "features/roleManagement/domain/entities/RoleRequestEntity";
import { RefObject } from "react";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { IRoleManagementObservedState } from "../interfaces/IRoleManagement";

class RoleInput implements Disposable {
  public fetchList = new BehaviorSubject<RoleRequestEntity>({});
  public fetchDetail = new BehaviorSubject<RoleRequestEntity>({});
  public fetchMenu = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchFeatures = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchUsers = new BehaviorSubject<BaseUserRequestModel>({});
  public fetchActions = new BehaviorSubject<{
    action?: "create" | "update" | "delete";
    value?: RoleActionRequestEntity;
    detailInfo?: DetailRoleEntity;
  }>({});

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.fetchDetail.complete();
    this.fetchMenu.complete();
    this.fetchFeatures.complete();
    this.fetchUsers.complete();
    this.fetchActions.complete();
  }
}

class RoleOutput implements Disposable {
  public onError = new BehaviorSubject<Error>({});
  public onGetDetailSuccess = new BehaviorSubject<boolean>(false);
  public onActionSuccess = new BehaviorSubject<{
    type?: "create" | "update" | "delete";
    body?: RoleActionRequestEntity;
  }>({});

  [Symbol.dispose](): void {
    this.onError.complete();
    this.onGetDetailSuccess.complete();
    this.onActionSuccess.complete();
  }
}

@injectable()
export class RoleManagementViewModel extends BasedObservedViewModel<
  RoleInput,
  RoleOutput,
  IRoleManagementObservedState
> {
  private roleUseCase = RoleManagementInjection.getUseCase();

  protected override createObservedState(): IRoleManagementObservedState {
    return {
      loading: false,
      list: [],
      detail: new DetailRoleEntity(),
      paging: {
        page: 0,
        limit: 10,
        total: 0,
      },
      menuList: [],
      userList: [],
      featureList: [],
    };
  }

  constructor() {
    super(new RoleInput(), new RoleOutput());
  }

  public buildTableQuery(
    pagination: any,
    sorter: SorterResult,
    filterForm: RefObject<FormInstance<any>>,
    prevParams: RoleRequestEntity,
  ) {
    const formValues = filterForm?.current?.getFieldsValue(true) || {};
    const { created, ...rest } = formValues;

    const order =
      sorter?.order === "descend"
        ? `${String(sorter?.column?.dataIndex)} DESC`
        : sorter?.order === "ascend"
          ? `${String(sorter?.column?.dataIndex)} ASC`
          : "createdAt DESC";

    const query = {
      ...prevParams,
      ...rest,
      created: created ?? prevParams?.created,
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
      order,
    };

    return {
      query,
      pagination: {
        page: pagination?.current - 1,
        limit: pagination?.pageSize,
      },
    };
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.setObservedState({ loading: true, list: [] });
        const res = await this.roleUseCase.getList(params);
        if (res?.data) {
          this.setObservedState({
            list: res.data,
            paging: res.pagination,
          });
        }
      } catch (error) {
        console.error(error);
        this.setObservedState({ list: [], paging: {} });
        this.output.onError.next(error);
      } finally {
        this.setObservedState({ loading: false });
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        this.setObservedState({ loading: true });
        const res = await this.roleUseCase.getRoleDetail(params);
        if (res?.data) {
          this.setObservedState({ detail: res.data });
          this.output.onGetDetailSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.setObservedState({ loading: false });
      }
    });

    this.skipFirstInitial(this.input.fetchFeatures, async (params) => {
      try {
        const res = await this.roleUseCase.getUserPermission(params);
        if (res) {
          this.setObservedState({ featureList: res });
        }
      } catch (error) {
        console.error(error);
      }
    });

    this.skipFirstInitial(this.input.fetchMenu, async (params) => {
      try {
        const res = await this.roleUseCase.getMenuList(params);
        if (res) {
          this.setObservedState({ menuList: res });
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchUsers, async (params) => {
      try {
        const res = await this.roleUseCase.getUsers(params);
        if (res) {
          this.setObservedState({ userList: res });
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchActions, async (params) => {
      try {
        this.setObservedState({ loading: true });
        const checkedBody = this.roleUseCase.updateBody(
          params?.value,
          params?.detailInfo,
        );

        if (params?.action === "create" && params?.value) {
          const res = await this.roleUseCase.createRole(
            params.value,
            params?.detailInfo,
          );
          if (res?.data) {
            this.output.onActionSuccess.next({
              type: "create",
              body: checkedBody?.afterBody,
            });
          }
        }

        if (params?.action === "update" && params?.value) {
          const res = await this.roleUseCase.updateRole(
            params.value,
            params?.detailInfo,
          );
          if (res?.data) {
            this.output.onActionSuccess.next({
              type: "update",
              body: checkedBody?.afterBody,
            });
          }
        }

        if (params?.action === "delete" && params?.value?.code) {
          const res = await this.roleUseCase.deleteRole({
            code: params.value.code,
          });
          if (res?.data) {
            this.output.onActionSuccess.next({
              type: "delete",
              body: {},
            });
          }
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.setObservedState({ loading: false });
      }
    });
  }
}
